import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isProvisionalMaterialCode } from '@obraplan/core/domain/schedule/material';
import type {
  PreviewPurchaseLinesFromTakeoffInput,
  PurchaseFromTakeoffLine,
  PurchaseFromTakeoffPreview,
  PurchaseFromTakeoffResult,
  PurchaseFromTakeoffStatus,
  TypologyItemTakeoffInput,
} from '@obraplan/core/application/contracts/purchase-from-takeoff';
import { LoadingState } from '@/components/feedback/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import type { QuantitativoMontado } from '@/features/schedule-items/use-takeoff-lines';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { formatBusinessDate } from '@/lib/format';
import { useCommand } from '@/lib/use-command';
import { ORIGIN_LABEL, formatPurchaseQuantity, plural } from './purchase-model';

/** Quantas linhas a prévia mostra — a Torre 2 inteira passa de mil. */
const LINHAS_NA_PREVIA = 300;

const STATUS_LABEL: Readonly<Record<PurchaseFromTakeoffStatus, string>> = {
  CREATE: 'nova',
  TAKE_OVER: 'assume a planilha',
  UPDATE: 'atualiza',
  UNCHANGED: 'igual',
  NO_ITEM: 'sem item de mão de obra',
  ERROR: 'não entra',
};

const STATUS_TONE: Readonly<
  Record<PurchaseFromTakeoffStatus, 'neutral' | 'info' | 'ok' | 'warn' | 'danger'>
> = {
  CREATE: 'ok',
  TAKE_OVER: 'info',
  UPDATE: 'info',
  UNCHANGED: 'neutral',
  NO_ITEM: 'warn',
  ERROR: 'danger',
};

/** O material do quantitativo, como a página o lê (`useQuantitativoMontado`). */
export type MateriaisDoQuantitativo = QuantitativoMontado<TypologyItemTakeoffInput>;

export interface GenerateMaterialsFromTakeoffDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly takeoff: MateriaisDoQuantitativo;
}

interface EstadoDaPrevia {
  readonly pedido: PreviewPurchaseLinesFromTakeoffInput;
  readonly previa: PurchaseFromTakeoffPreview | undefined;
  readonly erro: string | undefined;
  readonly carregando: boolean;
}

/**
 * "Gerar do quantitativo" no cronograma por item (Fase 35, RN-449 a RN-453).
 *
 * Uma linha por pavimento × serviço × material comprável do quantitativo, com a data que segue
 * o item de mão de obra (RN-451). A prévia diz o que entra, o que assume a linha da planilha e
 * o que fica de fora por falta do cronograma por mão de obra. **Nada é gravado até confirmar.**
 *
 * O código de compras que o catálogo do ObraPlan ainda não tem é conferido no compras (RN-450):
 * o que existe lá entra sem pedir nada, e o que lá não existe não entra. O resto — o item sem
 * código, que nasce provisório (`ORC-Q-…`), e o código que não deu para conferir — só com a
 * caixa marcada (RN-035). O provisório de uma geração anterior ganha o código pela mesma regra.
 */
export function GenerateMaterialsFromTakeoffDialog({
  open,
  onOpenChange,
  takeoff,
}: GenerateMaterialsFromTakeoffDialogProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const [criarMateriais, setCriarMateriais] = useState(false);
  const [estado, setEstado] = useState<EstadoDaPrevia | undefined>(undefined);

  const linhas = takeoff.estado === 'PRONTO' ? takeoff.linhas : undefined;
  const pedido =
    open && activeProjectId !== undefined && linhas !== undefined
      ? { projectId: activeProjectId, createMissingMaterials: criarMateriais, takeoff: [...linhas] }
      : undefined;
  const chave = pedido === undefined ? undefined : `${activeProjectId}|${String(criarMateriais)}`;

  useEffect(() => {
    if (pedido === undefined || session === undefined) return;
    let vivo = true;
    setEstado((atual) => ({ pedido, previa: atual?.previa, erro: undefined, carregando: true }));
    container.previewPurchaseLinesFromTakeoff.execute(pedido, session).then(
      (resposta) => {
        if (!vivo) return;
        setEstado((atual) =>
          resposta.ok
            ? { pedido, previa: resposta.value, erro: undefined, carregando: false }
            : { pedido, previa: atual?.previa, erro: resposta.error.message, carregando: false },
        );
      },
      () => {
        if (!vivo) return;
        setEstado((atual) => ({
          pedido,
          previa: atual?.previa,
          erro: 'Não foi possível calcular a prévia — verifique a conexão.',
          carregando: false,
        }));
      },
    );
    return () => {
      vivo = false;
    };
    // O pedido é refeito a cada render; a chave diz quando ele mudou de verdade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, linhas, session, container]);

  const [confirmar, confirmacao] = useCommand(
    (confirmado: PreviewPurchaseLinesFromTakeoffInput) =>
      container.confirmPurchaseLinesFromTakeoff.execute(confirmado, session!),
    {
      silent: true,
      successMessage: resultadoDaGeracao,
      onSuccess: () => fechar(),
    },
  );

  const fechar = (): void => {
    setCriarMateriais(false);
    setEstado(undefined);
    onOpenChange(false);
  };
  const aoMudarAberto = (proximo: boolean): void => {
    if (!proximo) {
      fechar();
      return;
    }
    onOpenChange(true);
  };

  const previa = estado?.previa;
  const resumo = previa?.summary;
  const aplicaveis = resumo === undefined ? 0 : resumo.create + resumo.takeOver + resumo.update;
  const atualizada = estado !== undefined && !estado.carregando && estado.erro === undefined;
  const motivoDoBloqueio =
    previa === undefined
      ? undefined
      : aplicaveis > 0
        ? undefined
        : resumo !== undefined && resumo.unchanged > 0
          ? 'Nada a mudar: as linhas do quantitativo já estão no cronograma.'
          : resumo !== undefined && resumo.noItem > 0
            ? 'Nada a gerar: falta o cronograma por mão de obra destes serviços.'
            : 'Nada a gerar: o quantitativo não tem material com quantidade nos pavimentos.';
  const podeConfirmar =
    pedido !== undefined && atualizada && aplicaveis > 0 && !confirmacao.isPending;

  return (
    <Dialog open={open} onOpenChange={aoMudarAberto}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Gerar do quantitativo</DialogTitle>
          <DialogDescription>
            Uma linha por pavimento × serviço × material do quantitativo. A data de necessidade
            segue o item do cronograma por mão de obra, menos a margem de chegada da obra. Nada é
            gravado até você confirmar.
          </DialogDescription>
        </DialogHeader>

        {takeoff.estado === 'CARREGANDO' || takeoff.estado === 'DESLIGADO' ? (
          <LoadingState label="Lendo o quantitativo da obra…" />
        ) : takeoff.estado === 'FALHOU' ? (
          <p role="alert" className="text-sm text-(--color-state-warn)">
            Não foi possível ler o quantitativo da obra: {takeoff.motivo}
          </p>
        ) : takeoff.estado === 'VAZIO' ? (
          <p role="alert" className="text-sm text-(--color-state-warn)">
            O quantitativo da obra não tem material: nenhum item comprável dos serviços tem
            quantidade nas tipologias.
          </p>
        ) : (
          <section aria-label="Prévia" className="flex min-w-0 flex-col gap-4">
            {estado?.erro !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {estado.erro}
              </p>
            )}
            {previa === undefined ? (
              estado?.erro === undefined && <LoadingState label="Calculando a prévia…" />
            ) : (
              <>
                <Resumo previa={previa} recalculando={estado?.carregando === true} />
                {previa.withoutLaborItem.length > 0 && <SemItem previa={previa} />}
                {(previa.newMaterials.length > 0 || previa.linkedMaterials.length > 0) && (
                  <NovosMateriais
                    previa={previa}
                    marcado={criarMateriais}
                    onChange={setCriarMateriais}
                  />
                )}
                <Avisos previa={previa} />
                <Linhas lines={previa.lines} />
              </>
            )}
            <p className="rounded-md bg-(--color-surface-muted) p-3 text-xs text-(--color-content-muted)">
              O quantitativo vence a planilha: a linha que veio da planilha passa a ter o número do
              quantitativo, e o número dela fica guardado para comparar. A ciência e a requisição da
              linha não mudam.
            </p>
            {confirmacao.error !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {confirmacao.error.message}
              </p>
            )}
          </section>
        )}

        <DialogFooter>
          {motivoDoBloqueio !== undefined && (
            <p className="order-last text-xs text-(--color-state-warn) sm:order-first sm:mr-auto sm:max-w-md sm:self-center">
              {motivoDoBloqueio}
            </p>
          )}
          <Button type="button" variant="secondary" onClick={() => aoMudarAberto(false)}>
            Cancelar
          </Button>
          {linhas !== undefined && (
            <Button
              type="button"
              variant="primary"
              disabled={!podeConfirmar}
              onClick={() => {
                if (pedido !== undefined) void confirmar(pedido);
              }}
            >
              {confirmacao.isPending ? 'Gerando…' : 'Gerar linhas'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Resumo({
  previa,
  recalculando,
}: {
  readonly previa: PurchaseFromTakeoffPreview;
  readonly recalculando: boolean;
}) {
  const { summary } = previa;
  const partes = [
    summary.create > 0 && plural(summary.create, 'nova', 'novas'),
    summary.takeOver > 0 && plural(summary.takeOver, 'assume a planilha', 'assumem a planilha'),
    summary.update > 0 && plural(summary.update, 'atualizada', 'atualizadas'),
    summary.unchanged > 0 && plural(summary.unchanged, 'igual', 'iguais'),
    summary.noItem > 0 && `${String(summary.noItem)} sem item de mão de obra`,
    summary.error > 0 && plural(summary.error, 'com erro', 'com erro'),
  ].filter((parte): parte is string => typeof parte === 'string');
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm" data-testid="resumo-da-previa">
        <span className="font-medium">{plural(summary.lines, 'linha', 'linhas')}</span>
        {partes.length > 0 && `: ${partes.join(' · ')}`}
      </p>
      {recalculando && (
        <span role="status" className="text-xs text-(--color-content-muted)">
          Recalculando…
        </span>
      )}
    </div>
  );
}

function SemItem({ previa }: { readonly previa: PurchaseFromTakeoffPreview }) {
  const lista = previa.withoutLaborItem;
  return (
    <div
      role="note"
      aria-label="Sem item de mão de obra"
      className="flex flex-col gap-2 rounded-md border border-(--color-state-warn) p-3"
    >
      <p className="text-sm">
        <span className="font-medium">
          {plural(lista.length, 'pavimento × serviço', 'pavimentos × serviços')} sem item no
          cronograma por mão de obra.
        </span>{' '}
        O material deles fica de fora até o item existir: gere antes o{' '}
        <Link to="/cronograma/itens" className="underline">
          cronograma por mão de obra
        </Link>
        .
      </p>
      <ul className="flex flex-wrap gap-1.5 text-xs">
        {lista.slice(0, 12).map((linha) => (
          <li
            key={`${linha.floorId}|${linha.serviceId}`}
            className="rounded-full border border-(--color-border-subtle) px-2 py-0.5"
          >
            {linha.towerName} · {linha.floorName} — {linha.serviceName} (
            {plural(linha.materials, 'material', 'materiais')})
          </li>
        ))}
        {lista.length > 12 && (
          <li className="px-2 py-0.5 text-(--color-content-muted)">e mais {lista.length - 12}</li>
        )}
      </ul>
    </div>
  );
}

function NovosMateriais({
  previa,
  marcado,
  onChange,
}: {
  readonly previa: PurchaseFromTakeoffPreview;
  readonly marcado: boolean;
  readonly onChange: (marcado: boolean) => void;
}) {
  // O que o compras confirmou entra sem a caixa (RN-450): só se diz. O resto pede a caixa.
  const conferidos = [
    ...previa.newMaterials
      .filter((material) => material.confirmed)
      .map((material) => ({ chave: material.code, nome: material.name, unit: material.unit })),
    ...previa.linkedMaterials
      .filter((material) => material.confirmed)
      .map((material) => ({
        chave: material.previousCode,
        nome: `${material.name} → ${material.code}`,
        unit: null,
      })),
  ];
  const novos = previa.newMaterials.filter((material) => !material.confirmed);
  const vincular = previa.linkedMaterials.filter((material) => !material.confirmed);
  const provisorios = novos.filter((material) => isProvisionalMaterialCode(material.code));
  const comCodigo = novos.length - provisorios.length;
  const criar =
    novos.length === 0
      ? undefined
      : provisorios.length === novos.length
        ? novos.length === 1
          ? 'criar o material provisório que falta'
          : `criar os ${String(novos.length)} materiais provisórios que faltam`
        : novos.length === 1
          ? 'criar o material que falta'
          : `criar os ${String(novos.length)} materiais que faltam`;
  const vinculo =
    vincular.length === 0
      ? undefined
      : vincular.length === 1
        ? 'dar o código de compras ao provisório'
        : `dar o código de compras aos ${String(vincular.length)} provisórios`;
  const rotulo = [criar, vinculo].filter((parte) => parte !== undefined).join(' e ');
  const pendentes = [
    ...novos.map((material) => ({
      chave: material.code,
      nome: material.name,
      unit: material.unit,
    })),
    ...vincular.map((material) => ({
      chave: material.previousCode,
      nome: `${material.name} → ${material.code}`,
      unit: null,
    })),
  ];
  return (
    <>
      {conferidos.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
          <p className="text-sm font-medium">
            {conferidos.length === 1
              ? '1 código conferido no sistema de compras'
              : `${String(conferidos.length)} códigos conferidos no sistema de compras`}
          </p>
          <p className="text-xs text-(--color-content-muted)">
            Existem e estão ativos no compras: entram no cadastro de materiais do ObraPlan com esse
            código ao gerar, sem precisar marcar nada.
          </p>
          <ListaDeMateriais rotulo="Conferidos no compras" materiais={conferidos} />
        </div>
      )}
      {pendentes.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
          <div className="flex items-start gap-2">
            <input
              id="gerar-criar-materiais"
              type="checkbox"
              className="mt-1"
              checked={marcado}
              onChange={(event) => onChange(event.target.checked)}
            />
            <div className="flex min-w-0 flex-col">
              <label htmlFor="gerar-criar-materiais" className="text-sm font-medium">
                {rotulo.charAt(0).toUpperCase() + rotulo.slice(1)}
              </label>
              {comCodigo > 0 && (
                <p className="text-xs text-(--color-content-muted)">
                  Itens com código de compras que não deu para conferir no compras: entram no
                  cadastro do ObraPlan com esse código.
                </p>
              )}
              {provisorios.length > 0 && (
                <p className="text-xs text-(--color-content-muted)">
                  Itens sem código de compras. Entram com código ORC-Q-…, marcados, e só viram
                  requisição depois de vinculados ao SKU na tela de Materiais.
                </p>
              )}
              {vincular.length > 0 && (
                <p className="text-xs text-(--color-content-muted)">
                  Materiais ORC-Q-… de uma geração anterior, de itens que têm código de compras:
                  passam a ter esse código, e as linhas que apontam para eles continuam apontando.
                </p>
              )}
            </div>
          </div>
          <ListaDeMateriais rotulo="Materiais do catálogo" materiais={pendentes} />
        </div>
      )}
    </>
  );
}

function ListaDeMateriais({
  rotulo,
  materiais,
}: {
  readonly rotulo: string;
  readonly materiais: readonly { chave: string; nome: string; unit: string | null }[];
}) {
  return (
    <ul className="flex flex-wrap gap-1.5 text-xs" aria-label={rotulo}>
      {materiais.slice(0, 12).map((material) => (
        <li
          key={material.chave}
          className="rounded-full border border-(--color-border-subtle) px-2 py-0.5"
        >
          {material.nome}
          {material.unit === null ? '' : ` (${material.unit})`}
        </li>
      ))}
      {materiais.length > 12 && (
        <li className="px-2 py-0.5 text-(--color-content-muted)">e mais {materiais.length - 12}</li>
      )}
    </ul>
  );
}

function Avisos({ previa }: { readonly previa: PurchaseFromTakeoffPreview }) {
  if (previa.unknownServices.length === 0 && previa.unplacedUnitTypes.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 text-xs text-(--color-state-warn)" aria-label="Avisos">
      {previa.unknownServices.length > 0 && (
        <li>
          Serviços que a obra não alcança, fora da geração:{' '}
          {previa.unknownServices
            .map((servico) => `${servico.disciplineName} · ${servico.serviceName}`)
            .join('; ')}
          .
        </li>
      )}
      {previa.unplacedUnitTypes.length > 0 && (
        <li>
          Tipologias com material que nenhum pavimento posiciona:{' '}
          {previa.unplacedUnitTypes.map((tipologia) => tipologia.name).join(', ')}.
        </li>
      )}
    </ul>
  );
}

function Linhas({ lines }: { readonly lines: readonly PurchaseFromTakeoffLine[] }) {
  const mostradas = lines.slice(0, LINHAS_NA_PREVIA);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <TableWrapper className="max-h-[22rem] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pavimento</TableHead>
              <TableHead>Serviço</TableHead>
              <TableHead>Material</TableHead>
              <TableHead className="text-right">Quantidade</TableHead>
              <TableHead>Necessário em</TableHead>
              <TableHead>Situação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mostradas.map((line) => (
              <TableRow
                key={`${line.floorId}|${line.serviceId}|${line.materialCode}`}
                data-testid="linha-da-previa"
              >
                <TableCell className="align-top whitespace-nowrap">
                  {line.towerName} · {line.floorName}
                </TableCell>
                <TableCell className="align-top">
                  <span className="text-(--color-content-muted)">{line.disciplineName} · </span>
                  {line.serviceName}
                </TableCell>
                <TableCell className="align-top">
                  <span className="font-medium">{line.materialName}</span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <span className="tabular text-xs text-(--color-content-muted)">
                      {line.materialCode}
                    </span>
                    {line.provisionalCode && <Badge tone="warn">código provisório</Badge>}
                  </span>
                </TableCell>
                <TableCell className="text-right align-top whitespace-nowrap tabular">
                  <Quantidade line={line} />
                </TableCell>
                <TableCell className="align-top whitespace-nowrap tabular">
                  {line.requiredDate === null ? '—' : formatBusinessDate(line.requiredDate)}
                </TableCell>
                <TableCell className="align-top">
                  <Badge tone={STATUS_TONE[line.status]}>{STATUS_LABEL[line.status]}</Badge>
                  {line.message !== null && (
                    <span className="mt-0.5 block text-xs text-(--color-state-danger)">
                      {line.message}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
      {lines.length > mostradas.length && (
        <p className="text-xs text-(--color-content-muted)">
          Mostrando {LINHAS_NA_PREVIA} de {lines.length.toLocaleString('pt-BR')} linhas.
        </p>
      )}
    </div>
  );
}

function Quantidade({ line }: { readonly line: PurchaseFromTakeoffLine }) {
  const unidade = line.unit ?? '';
  const agora = formatPurchaseQuantity(line.quantity, unidade, false);
  if (
    line.currentQuantity === null ||
    line.status === 'UNCHANGED' ||
    line.currentQuantity === line.quantity
  ) {
    return <>{agora}</>;
  }
  const origem =
    line.currentOrigin !== null && line.currentOrigin in ORIGIN_LABEL
      ? ORIGIN_LABEL[line.currentOrigin as keyof typeof ORIGIN_LABEL].toLowerCase()
      : 'hoje';
  return (
    <>
      {agora}
      <span className="block text-xs text-(--color-content-muted)">
        {origem}: {formatPurchaseQuantity(line.currentQuantity, unidade, false)}
      </span>
    </>
  );
}

/** A frase do toast depois de gerar. */
function resultadoDaGeracao(resultado: PurchaseFromTakeoffResult): string {
  const partes = [
    resultado.created > 0 && plural(resultado.created, 'linha criada', 'linhas criadas'),
    resultado.takenOver > 0 &&
      plural(resultado.takenOver, 'assumida da planilha', 'assumidas da planilha'),
    resultado.updated > 0 && plural(resultado.updated, 'atualizada', 'atualizadas'),
    resultado.createdMaterials > 0 &&
      plural(resultado.createdMaterials, 'material criado', 'materiais criados'),
    resultado.linkedMaterials > 0 &&
      plural(
        resultado.linkedMaterials,
        'provisório com o código de compras',
        'provisórios com o código de compras',
      ),
  ].filter((parte): parte is string => typeof parte === 'string');
  return partes.length === 0
    ? 'Nada mudou: o cronograma por item já tinha o quantitativo.'
    : `Cronograma por item gerado do quantitativo: ${partes.join(', ')}.`;
}
